/* eslint-disable prefer-destructuring */
/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-cycle */
/* eslint-disable no-unused-vars */
/* eslint-disable require-yield */
import { call, put, all, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { get, take } from 'lodash';
import * as actions from './actions';
import * as types from '../types';
import axios from '../../../services/axios';
// eslint-disable-next-line import/no-useless-path-segments
import store from '../../../store/index';
import { endSession, clearLocalSession } from './sessionCleanup';

// Interceptor para lidar com erros 401/403
let handling403 = false;
let isRefreshing = false;
let refreshQueue = [];

// ─── Tolerância aos dois shapes de erro do backend ──────────────────────────
// Legado:  { error: 'Sem sessão' } ou { errors: ['msg'] }
// Novo (error handler global, JSend): { status: 'fail', message: '...',
//    errors: [{ field, message, code }] }
// Extrai mensagens + codes de qualquer um dos formatos pra que a detecção de
// "sessão morta" não quebre durante a transição de contrato.
function extractErrorSignals(err) {
   const data = get(err, 'response.data', {}) || {};
   const messages = [];
   const codes = [];
   if (typeof data.error === 'string') messages.push(data.error);
   if (typeof data.message === 'string') messages.push(data.message);
   const list = Array.isArray(data.errors) ? data.errors : [];
   list.forEach((entry) => {
      if (typeof entry === 'string') {
         messages.push(entry);
      } else if (entry) {
         if (entry.message) messages.push(entry.message);
         if (entry.code) codes.push(entry.code);
      }
   });
   // No shape novo a mesma mensagem pode vir em data.message E em
   // errors[].message — dedup evita toast duplicado.
   return {
      messages: Array.from(new Set(messages)),
      codes: Array.from(new Set(codes)),
   };
}

// Strings que o backend usa pra AFIRMAR que a sessão morreu (legado) +
// codes equivalentes do novo error handler. No /auth/refresh, qualquer um
// desses significa "refresh cookie ausente/inválido/expirado" → logout.
const SESSION_DEAD_MESSAGES = [
   'Sem sessão',
   'Sessão inválida',
   'Sessão expirada',
];
const SESSION_DEAD_CODES = [
   'missing_token',
   'invalid_token',
   'session_invalid',
   'session_expired',
];
const ORIGIN_BLOCKED_MESSAGES = ['Origem inválida', 'Origem ausente'];

// ⚠️ Este set responde "o REFRESH falhou de forma fatal?" — ele só é consultado
// no catch do /auth/refresh, onde `invalid_token`/`missing_token` significam
// "cookie rt ausente ou inválido". NÃO serve para decidir nada sobre o 401 de um
// request comum: ali `invalid_token` é o access token de 15 min expirado, que é
// exatamente o caso que DEVE ser renovado. Ver `SESSION_REVOKED_CODES`.
function isSessionDeadError(err) {
   const status = get(err, 'response.status', 0);
   if (status !== 401) return false;
   const { messages, codes } = extractErrorSignals(err);
   return (
      messages.some((m) => SESSION_DEAD_MESSAGES.includes(m)) ||
      codes.some((c) => SESSION_DEAD_CODES.includes(c))
   );
}

// ─── Sessão revogada × token legado (handoff 0097 §2.1) ─────────────────────
// O backend separou os dois estados que antes compartilhavam `session_invalid`:
//
//   session_invalid → a versão do token divergiu: a sessão foi REVOGADA de
//                     verdade (troca de senha, reset). O /auth/refresh também
//                     falharia, porque `tokenVersion` só é incrementado junto
//                     com a revogação das auth_sessions, na mesma transação.
//                     Tentar renovar é round-trip garantidamente perdido →
//                     vai direto para o login.
//
//   token_outdated  → token emitido antes do deploy, sem o claim
//                     `token_version` (Etapa B). A sessão está VIVA: cai no
//                     caminho normal de refresh + retry, e o cutover fica
//                     invisível para o usuário.
//
// Por isso o set abaixo tem UM item só, e `token_outdated` está deliberadamente
// fora dele (e fora de SESSION_DEAD_CODES).
const SESSION_REVOKED_CODES = ['session_invalid'];

function isSessionRevokedError(err) {
   const status = get(err, 'response.status', 0);
   if (status !== 401) return false;
   const { codes } = extractErrorSignals(err);
   // Só por `code` — nunca por texto de mensagem. A mensagem de sessão revogada
   // ("Sessão expirada. Faça login novamente.") é indistinguível da de token
   // expirado comum, que precisa de refresh.
   return codes.some((c) => SESSION_REVOKED_CODES.includes(c));
}

function isOriginBlockedError(err) {
   const status = get(err, 'response.status', 0);
   if (status !== 401) return false;
   const { messages } = extractErrorSignals(err);
   return messages.some((m) => ORIGIN_BLOCKED_MESSAGES.includes(m));
}

axios.interceptors.response.use(
   (response) => response,
   async (error) => {
      const status = get(error, 'response.status', 0);
      const reqUrl = get(error, 'config.url', '');

      // 5xx do novo error handler global carregam error_id (UUID) — logar no
      // console ajuda o suporte a cruzar com o X-Request-Id do backend.
      if (status >= 500) {
         const errorId = get(error, 'response.data.error_id', null);
         if (errorId) {
            // eslint-disable-next-line no-console
            console.error(`[API ${status}] error_id=${errorId} (${reqUrl})`);
         }
      }

      function normalizeAuth(data) {
         const token = data?.token || data?.accessToken;
         const user = data?.user || {};
         const permissions = data?.permissions || user?.permissions || [];
         return { token, user, permissions };
      }

      function resolveQueue(err, token) {
         refreshQueue.forEach(({ resolve, reject }) => {
            if (err) reject(err);
            else resolve(token);
         });
         refreshQueue = [];
      }

      if (status === 401) {
         const originalConfig = error.config || {};

         // Requisições que não devem tentar refresh — passam o erro adiante direto
         if (originalConfig._skipRefresh) {
            return Promise.reject(error);
         }

         if (
            reqUrl.includes('/auth/login') ||
            reqUrl.includes('/auth/2fa/verify') ||
            reqUrl.includes('/auth/refresh')
         ) {
            return Promise.reject(error);
         }

         // ✅ sessão REVOGADA (handoff 0097 §2.1): não adianta renovar — o
         // refresh falharia também. Corta o round-trip e o flicker.
         if (isSessionRevokedError(error)) {
            toast.error('Sua sessão foi encerrada. Faça login novamente.');
            endSession(store.dispatch, { delayMs: 1000 });
            return Promise.reject(error);
         }

         // evita loop infinito
         if (originalConfig._retry) {
            return Promise.reject(error);
         }
         originalConfig._retry = true;

         // se já tem refresh rodando, entra na fila
         if (isRefreshing) {
            return new Promise((resolve, reject) => {
               refreshQueue.push({ resolve, reject });
            }).then((newToken) => {
               originalConfig.headers = originalConfig.headers || {};
               originalConfig.headers.Authorization = `Bearer ${newToken}`;
               return axios(originalConfig);
            });
         }

         isRefreshing = true;

         try {
            const resp = await axios.post('/auth/refresh', null, {
               _skipRefresh: true,
            });

            const auth = normalizeAuth(resp.data);

            // atualiza header
            axios.defaults.headers.Authorization = `Bearer ${auth.token}`;

            // atualiza redux mantendo RBAC
            store.dispatch(actions.loginSuccess(auth));

            resolveQueue(null, auth.token);

            // repete request original
            originalConfig.headers = originalConfig.headers || {};
            originalConfig.headers.Authorization = `Bearer ${auth.token}`;
            return axios(originalConfig);
         } catch (refreshErr) {
            resolveQueue(refreshErr, null);

            // ✅ só considera "sessão expirada" quando o backend AFIRMA isso
            // (mensagem legada OU code do novo error handler global)
            const isSessionDead = isSessionDeadError(refreshErr);

            // (opcional) origem não permitida é um caso separado
            const isOriginBlocked = isOriginBlockedError(refreshErr);

            if (isSessionDead) {
               toast.error('Sua sessão expirou. Faça login novamente.');
               endSession(store.dispatch, { delayMs: 1000 });

               return Promise.reject(refreshErr);
            }

            if (isOriginBlocked) {
               // aqui é melhor ser explícito (não é sessão expirada)
               toast.error(
                  'Origem não permitida para autenticação. Verifique a URL do sistema.'
               );
               return Promise.reject(refreshErr);
            }

            // ✅ falha transitória (restart/timeout/502/etc):
            // NÃO desloga, porque a sessão pode estar válida.
            toast.warn('Servidor indisponível no momento. Tente novamente.');
            return Promise.reject(refreshErr);
         } finally {
            isRefreshing = false;
         }
      }

      // ✅ 403 = sem permissão -> manda pra /403
      //
      // Porta de saída opcional (2026-08-22), no mesmo padrão do
      // `_skipRefresh`: existem DOIS 403 diferentes e o redirect só serve para
      // um deles.
      //   · 403 de ROTA  — "esta tela não é sua"    -> redirect faz sentido;
      //   · 403 de AÇÃO  — "esta operação não é sua" -> a tela continua válida
      //     e o usuário tem trabalho em mãos. Aqui o redirect é destrutivo:
      //     `window.location.href` recarrega a página e leva junto o
      //     formulário preenchido (ex.: conferência de transferência com dez
      //     itens contados e motivos digitados).
      //
      // Quem marca `_skipForbiddenRedirect` assume a exibição do erro —
      // tipicamente `notifyApiError`, que já tem mensagem para
      // `pdv_access_denied`. Isto EXTENDE o interceptor, não reescreve a
      // lógica de 401/403 (FC1 §A.9): sem a flag, tudo segue como antes.
      if (status === 403 && error.config?._skipForbiddenRedirect) {
         return Promise.reject(error);
      }

      if (status === 403) {
         if (!handling403) {
            handling403 = true;

            setTimeout(() => {
               if (window.location.pathname !== '/403') {
                  window.location.href = '/403';
               }
               handling403 = false;
            }, 0);
         }

         return Promise.reject(error);
      }

      return Promise.reject(error);
   }
);

function* loginRequest({ payload }) {
   const { email, password, prevPath, navigate } = payload;
   try {
      // Em desenvolvimento (NODE_ENV=development ou SKIP_2FA=true no backend),
      // o AuthController responde ao /auth/login sem exigir 2FA e já retorna o token.
      // Em produção, retorna preAuthId e o frontend exibe o modal de código.
      const resp = yield call(axios.post, '/auth/login', { email, password });

      // Desenvolvimento (bypass ativo no backend): token já vem na resposta
      if (resp.data._dev_bypass) {
         const token = resp.data.token || resp.data.accessToken;
         axios.defaults.headers.Authorization = `Bearer ${token}`; // eslint-disable-line
         yield put(
            actions.loginSuccess({
               token,
               user: resp.data.user,
               permissions: resp.data.user?.permissions || [],
            })
         );
         toast.success('Login bem-sucedido.');
         navigate(prevPath || '/main');
         return;
      }

      // Produção: aguarda verificação do código 2FA
      const preAuthId = resp.data.preAuthId;
      yield put(actions.auth2FARequired({ preAuthId, email }));
      toast.info('Código enviado para seu e-mail.');
   } catch (err) {
      toast.error('Usuário ou senha inválidos.');
      yield put(actions.loginFailure());
   }
}

function* auth2FAVerifyRequest({ payload }) {
   const { preAuthId, code, prevPath, navigate } = payload;

   try {
      const resp = yield call(axios.post, '/auth/2fa/verify', {
         preAuthId,
         code,
      });

      const token = resp.data.token || resp.data.accessToken;

      axios.defaults.headers.Authorization = `Bearer ${token}`; // eslint-disable-line

      // mantém o formato que seu reducer espera (token + user + permissions opcional)
      yield put(
         actions.loginSuccess({
            token,
            user: resp.data.user,
            permissions:
               resp.data.permissions || resp.data.user?.permissions || [],
         })
      );

      // fecha modal / limpa preAuthId
      yield put(actions.auth2FAClear());

      toast.success('Login bem-sucedido.');
      navigate(prevPath || '/main');
   } catch (err) {
      const status = get(err, 'response.status', 0);

      if (status === 410) toast.error('Código expirou. Gere um novo código.');
      else if (status === 429)
         toast.error('Muitas tentativas. Gere um novo código.');
      else toast.error('Código inválido.');

      yield put(actions.auth2FAVerifyFailure());
   }
}

function* auth2FAResendRequest({ payload }) {
   const { email, password } = payload;

   try {
      const resp = yield call(axios.post, '/auth/login', { email, password });
      const preAuthId = resp.data.preAuthId;

      yield put(actions.auth2FARequired({ preAuthId, email }));
      toast.info('Código reenviado para seu e-mail.');
   } catch (err) {
      toast.error(
         'Não foi possível reenviar o código. Verifique suas credenciais.'
      );
      yield put(actions.auth2FAResendFailure());
   }
}

function* logoutRequest({ payload }) {
   try {
      // Logout REAL: revoga a sessão do cookie `rt`. O `POST /logout` legado
      // (fora de /auth) nunca revogou nada e está depreciado no backend
      // (handoff 0097 §5, KILL-015) — este app nunca o chamou.
      yield call(axios.post, '/auth/logout');
   } catch (err) {
      toast.error(err.message);
   } finally {
      toast.success('Logout realizado com sucesso.');
      // Encerramento local centralizado — ver `sessionCleanup.js`.
      yield call(endSession, store.dispatch);
   }
}

function* persistRehydrate({ payload }) {
   const token = get(payload, 'auth.token', '');

   // Restaura o token no axios imediatamente para não quebrar requests feitas
   // logo após o rehydrate, antes do refresh completar
   if (token) axios.defaults.headers.Authorization = `Bearer ${token}`; // eslint-disable-line

   // Em desenvolvimento pode-se pular o refresh automático (ex: Insomnia, testes locais)
   if (process.env.REACT_APP_SKIP_REFRESH === 'true') return;

   // Lock contra refresh paralelo: Ctrl+Shift+R ou múltiplas abas podem disparar
   // persistRehydrate simultaneamente. Se o interceptor já está renovando a sessão,
   // aguardamos o resultado dele ao invés de abrir uma segunda requisição que
   // chegaria ao backend com o token pai já rotacionado pela primeira.
   if (isRefreshing) {
      try {
         const newTokenFromQueue = yield new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
         });
         if (newTokenFromQueue) {
            axios.defaults.headers.Authorization = `Bearer ${newTokenFromQueue}`; // eslint-disable-line
         }
      } catch {
         // o interceptor já tratou o logout se necessário; não fazemos nada aqui
      }
      return;
   }

   try {
      // Renova sessão silenciosamente via cookie rt (httpOnly, enviado automaticamente)
      const resp = yield call(axios.post, '/auth/refresh', null, {
         _skipRefresh: true,
      });

      const data = resp.data || {};
      const newToken = data.token || data.accessToken;

      if (!newToken) return;

      axios.defaults.headers.Authorization = `Bearer ${newToken}`; // eslint-disable-line

      yield put(
         actions.loginSuccess({
            token: newToken,
            user: data.user,
            permissions: data.permissions || data.user?.permissions || [],
         })
      );
   } catch (err) {
      // Tolerante aos dois shapes de erro (legado e JSend novo) — ver helpers
      // no topo do arquivo.
      const isSessionDead = isSessionDeadError(err);

      if (isSessionDead) {
         // No boot não redireciona: o PrivateRoute já leva ao login sozinho
         // quando `isLoggedIn` cai. Só limpa o rastro local.
         yield put(actions.logoutSuccess());
         yield call(clearLocalSession);
      }
      // Erro transitório (servidor reiniciando, timeout, 502):
      // mantém o estado atual e deixa o usuário seguir — o próximo request ou
      // refresh resolve. Não desloga preventivamente.
   }
}

// O saga `registerRequest` vivia aqui e chamava `PUT /users` (sem id) para o
// auto-serviço da tela Register em modo edição. Essa rota NUNCA existiu no
// backend — sempre respondeu 404 (handoff 0097 §6). Substituída por
// `PUT /users/me` (perfil) e `PUT /users/me/password` (senha), consumidas
// direto pelas telas de Conta via service, sem passar por saga.

export default all([
   takeLatest(types.LOGIN_REQUEST, loginRequest),
   takeLatest(types.AUTH_2FA_VERIFY_REQUEST, auth2FAVerifyRequest),
   takeLatest(types.AUTH_2FA_RESEND_REQUEST, auth2FAResendRequest),
   takeLatest(types.PERSIST_REHYDRATE, persistRehydrate),
   takeLatest(types.LOGOUT_REQUEST, logoutRequest),
]);
